/******************************************************************************

Copyright (c) 2016-2018, Highsoft

Permission is hereby granted, free of charge, to any person obtaining
a copy of this software and associated documentation files (the
"Software"), to deal in the Software without restriction, including
without limitation the rights to use, copy, modify, merge, publish,
distribute, sublicense, and/or sell copies of the Software, and to
permit persons to whom the Software is furnished to do so, subject to
the following conditions:

The above copyright notice and this permission notice shall be
included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT.
IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY
CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT,
TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.

******************************************************************************/

// @format

/**
 * Module for linking the chart to the advanced section
 * When hovering over chart elements, it will highlight the
 * appropriate fields in the advanced section
 */

import highed from './../globals';
import Events from './../core/highed.events';
import { getAttr, setAttr, isArr, isObj } from './../core/highcharts-editor';
import { cr, crSvg, ap, setStyle, on, getElementPosition, getElementSize, nodefault } from './../core/highed.dom';
import elements from './../meta/highed.inspector.config';
import store from 'redux/store';

export default () => {
  let events = Events();
  const TIMER = 800;
  const SCROLL_DURATION = 0.7;
  const HIGHLIGHT_BUFFER = 200;
  const HIGHLIGHT_TIMER = 1300;
  let isActive = false;
  const stopFill = ['line', 'area', 'areaspline'];
  let binders = [];
  let highchartsRoot = false;
  let newNodes = [];
  let duplicateXValues = [];
  let duplicateYValues = [];
  let changedNodeFill = [];
  let chart = false;
  let chartConstr = false;
  let isStock = false;
  let isMap = false;
  ///////////////////////////////////////////////////////////////////////////

  function scrollToElm(container, elm, duration) {
    var pos = getRelativePos(container, elm);
    scrollTo(container, pos.top, duration); // duration in seconds
  }

  function getRelativePos(container, elm) {
    var pPos = container.getBoundingClientRect(), // parent pos
      cPos = elm.getBoundingClientRect(), // target pos
      pos = {};

    (pos.top = cPos.top - pPos.top + container.scrollTop),
      (pos.right = cPos.right - pPos.right),
      (pos.bottom = cPos.bottom - pPos.bottom),
      (pos.left = cPos.left - pPos.left);

    return pos;
  }

  function scrollTo(element, to, duration) {
    var start = element.scrollTop,
      change = to - start,
      startTime = performance.now(),
      val,
      now,
      elapsed,
      t;

    function animateScroll() {
      now = performance.now();
      elapsed = (now - startTime) / 1000;
      t = elapsed / duration;

      element.scrollTop = start + change * easeInOutQuad(t);

      if (t < 1) window.requestAnimationFrame(animateScroll);
    }

    animateScroll();
  }

  function easeInOutQuad(t) {
    return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
  }

  function createAndAppendDummyElement(className, styling) {
    const element = createDummyElement(className, styling);
    ap(highchartsRoot, element);
    return element;
  }

  function createDummyElement(className, styling) {
    const element = crSvg('rect', className);
    element.setAttribute('fill', 'transparent');
    Object.keys(styling).forEach((key) => element.setAttribute(key, styling[key]));
    element.setAttribute('aria-hidden', true);
    return element;
  }

  function createAxesElements(container, type, svgBox, axesType) {
    const box = container.getBoundingClientRect();

    return createAndAppendDummyElement('everviz-dummy-rect ' + type + '-' + axesType, {
      'data-z-index': '10',
      x: box.left - svgBox.left,
      y: box.top - svgBox.top,
      width: box.width,
      height: box.height
    });
  }

  function createAxisLine(container, type, beforeElement, inverted) {
    const box = container.getBBox();
    const strokeWidth = parseInt(container.getAttribute('stroke-width'));

    let width = box.width;
    let height = box.height;
    let x = box.x;
    let y = box.y;

    if (strokeWidth) {
      if (type[0] === 'y') {
        if (inverted) {
          height += 4 + strokeWidth;
          y = y - height / 2;
        } else {
          width += 4 + strokeWidth;
          x = x - width / 2;
        }
      } else {
        if (inverted) {
          width += 4 + strokeWidth;
          x = x - width / 2;
        } else {
          height += 4 + strokeWidth;
          y = y - height / 2;
        }
      }
    }

    const element = createDummyElement('everviz-dummy-rect ' + type + '-line', {
      x: x,
      y: y,
      width: width,
      height: height
    });

    highchartsRoot.insertBefore(element, beforeElement);
    return element;
  }

  function createLineElements(container, type, svgBox, beforeElement, inverted, lineType) {
    const box = container.getBoundingClientRect();
    const strokeWidth = parseInt(container.getAttribute('stroke-width'));

    let width = box.width;
    let height = box.height;
    let x = box.left - svgBox.left;
    let y = box.top - svgBox.top;

    if (strokeWidth) {
      if (type[0] === 'y') {
        if (inverted) {
          width += 4 + strokeWidth;
          x = x - width / 2;
        } else {
          height += 4 + strokeWidth;
          y = y - height / 2;
        }
      } else {
        if (inverted) {
          height += 4 + strokeWidth;
          y = y - height / 2;
        } else {
          width += 4 + strokeWidth;
          x = x - width / 2;
        }
      }
    }

    const element = createDummyElement('everviz-dummy-rect ' + type + '-' + lineType, {
      x: x,
      y: y,
      width: width,
      height: height
    });

    highchartsRoot.insertBefore(element, beforeElement);
    return element;
  }

  function setupAxisLine(inverted) {
    const xAxisLine = highchartsRoot.querySelectorAll('.highcharts-axis.highcharts-xaxis .highcharts-axis-line');
    const yAxisLine = highchartsRoot.querySelectorAll('.highcharts-axis.highcharts-yaxis .highcharts-axis-line');
    const seriesGroup = highchartsRoot.querySelector('.highcharts-series-group');

    xAxisLine.forEach((line) => duplicateXValues.push(createAxisLine(line, 'xaxis', seriesGroup, inverted)));
    yAxisLine.forEach((line) => duplicateYValues.push(createAxisLine(line, 'yaxis', seriesGroup, inverted)));
  }

  function setupAxis(type, arr, svgBox, inverted, disableYContainer) {
    const vals = highchartsRoot.querySelectorAll('.highcharts-axis-labels.highcharts-' + type + '-labels text');
    const containers = highchartsRoot.querySelectorAll('.highcharts-axis-labels.highcharts-' + type + '-labels');
    const gridLines = highchartsRoot.querySelectorAll(
      '.highcharts-grid.highcharts-' + type + '-grid .highcharts-grid-line'
    );
    const ticks = highchartsRoot.querySelectorAll('.highcharts-axis.highcharts-' + type + ' .highcharts-tick');
    const seriesGroup = highchartsRoot.querySelector('.highcharts-series-group');

    arr.forEach((val) => {
      if (val) val.remove();
    });

    if (arr) arr.splice(0, arr.length);

    if (!disableYContainer)
      containers.forEach((container) => arr.push(createAxesElements(container, type, svgBox, 'container')));
    vals.forEach((val) => arr.push(createAxesElements(val, type, svgBox, 'label')));
    gridLines.forEach((line) => arr.push(createLineElements(line, type, svgBox, seriesGroup, inverted, 'gridline')));
    ticks.forEach((tick) => arr.push(createLineElements(tick, type, svgBox, seriesGroup, inverted, 'tick')));
  }

  function setupDummyRects(chart) {
    const svgBox = highchartsRoot.getBoundingClientRect();
    setupAxis('xaxis', duplicateXValues, svgBox, chart.inverted);
    setupAxis('yaxis', duplicateYValues, svgBox, chart.inverted, isStock);

    //Has to be done after gridlines for zindex
    setupAxisLine(chart.inverted);
  }

  function makeElementTransparent(node) {
    //Some elements in highcharts are set to fill: none
    //This makes their width/height not be counted so we cant highlight
    //Make them transparent instead
    const fill = node.getAttribute('fill');
    if (fill === 'none') {
      node.setAttribute('fill', 'transparent');
      changedNodeFill.push(node);
    }
  }

  function handleClick(option, index, node) {
    if (!isActive) return;

    let steps = option.steps;

    if (option.isSeries) {
      const classList = [...node.classList];
      //Index passed into this function might be unreliable due to the index prop in series.

      let seriesIndex = 0;
      classList.some((className) => {
        if (className.startsWith('highcharts-series-')) {
          const splitClassName = className.split('-');
          seriesIndex = parseInt(splitClassName[splitClassName.length - 1]);
          return true;
        }
      });

      let opt = false;
      if (chart.userOptions) opt = chart.userOptions;
      const types = (opt.series || []).map((series) => series.type || (opt.chart && opt.chart.type) || 'line');
      const type = types[seriesIndex];

      steps[1].element = '.node#plotOptions\\.' + type;
    }

    if (isMap && option.mapSteps) {
      steps = option.mapSteps;
    }

    steps.forEach((step, i) => {
      const scrollToTop = step.scrollToTop;

      setTimeout(() => {
        if (!step.parent) step.parent = '.left-advance-panel';

        const id = step.element;
        const element = document.querySelector(id);
        const parentElement = document.querySelector(step.parent);

        if (element) {
          element.click();
          scrollToElm(parentElement, element, SCROLL_DURATION);
        }
      }, TIMER * i);

      if (scrollToTop) {
        const rightPanel = document.querySelector('.right-advance-panel');
        rightPanel.scrollTo(0, 0);
      }
    });

    if (option.highlights) {
      setTimeout(() => {
        option.highlights.forEach((highlight) => {
          const element = document.querySelector(highlight);
          let addBgBack = false;
          if (element) {
            const classes = element.className.split(' ');
            if (classes.includes('bg-white')) {
              addBgBack = true;
              element.classList.remove('bg-white');
            }
            element.className += ' active-highlight';
          }
          setTimeout(() => {
            option.highlights.forEach((highlight) => {
              const element = document.querySelector(highlight);
              if (element) {
                element.classList.remove('active-highlight');
                if (addBgBack) element.className += ' bg-white';
              }
            });
          }, HIGHLIGHT_TIMER);
        });
      }, TIMER * steps.length + HIGHLIGHT_BUFFER);
    }
  }

  function useOriginalField(option, nodes, node, index) {
    node.setAttribute('class', node.getAttribute('class') + ' hovered');

    const nodeClickEvent = on(node, 'click', function (e) {
      handleClick(option, index, node);
      return;
    });

    on(node, 'mouseout', function () {
      nodes.forEach((node) => {
        node.setAttribute('class', node.getAttribute('class').replace(' hovered', ''));
      });
      nodeClickEvent();
    });
  }

  function createNewField(option, node, index) {
    let newNode = node.cloneNode(true);
    if (option.convertToRect) {
      if (option.useCalculatedValue) {
        const nodeVals = node.getBoundingClientRect();
        const parentVals = highchartsRoot.getBoundingClientRect();
        newNode = createDummyElement('everviz-dummy-rect', {
          x: nodeVals.x - parentVals.x,
          y: nodeVals.y - parentVals.y,
          width: nodeVals.width,
          height: nodeVals.height
        });
      } else if (node.getBBox) {
        const box = node.getBBox();
        newNode = createDummyElement('everviz-dummy-rect', {
          x: box.x,
          y: box.y,
          width: box.width,
          height: box.height
        });
      } else {
        //Probably has useHTML set on field, ex: title, subtitle
        const box = getElementPosition(node);
        const size = getElementSize(node);
        newNode = createDummyElement('everviz-dummy-rect', {
          x: box.x,
          y: box.y,
          width: size.w,
          height: size.h
        });
      }
    }

    newNode.setAttribute('class', 'everviz-new-node everviz-advanced-highlighter-path');
    newNode.style.outline = '2px solid #a2c6e7';

    const nodesToBind = [newNode];
    if (option.canClickElement) nodesToBind.push(node);

    on(nodesToBind, 'click', function () {
      handleClick(option, index, node);
    });

    if (option.before) {
      highchartsRoot.insertBefore(newNode, document.querySelector(option.before));
    } else if (option.after) {
      highchartsRoot.insertBefore(newNode, document.querySelector(option.after).nextSibling);
    } else ap(highchartsRoot, newNode);

    newNodes.push(newNode);
  }

  function createField(option, nodes, node, index) {
    if (option.useOriginal) {
      useOriginalField(option, nodes, node, index);
      return;
    }
    createNewField(option, node, index);
  }

  function attachAdvancedLink() {
    const beforeElement = highchartsRoot.querySelector('.highcharts-series-group');

    Object.keys(elements).forEach(function (elementKey) {
      const option = elements[elementKey];

      const nodes = [...document.querySelectorAll(elementKey)];
      if (nodes.length) {
        nodes.forEach((node, index) => {
          if (option.makeTransparent) makeElementTransparent(node);

          const onBind = on(node, 'mouseover', (e) => {
            nodefault(e);
            if (newNodes.length) {
              newNodes.forEach((newNode) => newNode.remove());
              newNodes = [];
            }

            if (option.isSeries) {
              let opt = false;
              if (chart.userOptions) opt = chart.userOptions;

              const types = (opt.series || []).map((series) => series.type || (opt.chart && opt.chart.type) || 'line');

              let newNode = false;
              let extraClass = '';
              let fillOnTemplate = stopFill.indexOf(types[index]) === -1;

              if (fillOnTemplate) extraClass = ' fill-segment';
              newNode = node.cloneNode(true);

              newNode.setAttribute(
                'class',
                'everviz-new-node everviz-dummy-series everviz-advanced-highlighter-path ' + extraClass
              );

              const nodesToBind = [newNode];
              if (option.canClickElement) nodesToBind.push(node);

              on(nodesToBind, 'click', function () {
                handleClick(option, index, node);
              });

              ap(highchartsRoot, newNode);
              highchartsRoot.insertBefore(newNode, beforeElement.nextSibling);
              newNodes.push(newNode);

              return;
            }

            if (option.highlightAll) {
              nodes.forEach((node) => createField(option, nodes, node, index));
            } else createField(option, nodes, node, index);
          });

          binders.push(onBind);
        });
      }
    });
  }

  function toggleActive(toggle) {
    isActive = toggle;

    if (toggle) init(true);
    else destroy(true);
  }

  function init(changeChartSettings) {
    if (!isActive) return;

    highchartsRoot = document.querySelector('svg.highcharts-root');
    if (!highchartsRoot) return;

    const { plugins } = store.getState().projectConfig;
    const { chart: projectChart, constr } = store.getState().chartEditorPage;

    chart = projectChart;

    if (chart && chart.polar) return;

    highchartsRoot.setAttribute('class', highchartsRoot.getAttribute('class') + ' in-inspector-mode');

    if (chart) {
      chartConstr = constr ?? 'Chart';
      isStock = chartConstr[0] === 'S';
      isMap = chartConstr[0] === 'M';

      if (!isStock && chart.series && chart.series[0] && chart.series[0].points) {
        const pointLength = chart.series[0].points.length;
        if (pointLength) chart.tooltip.refresh([chart.series[0].points[parseInt(pointLength / 2)]]);
        else chart.tooltip.refresh([chart.series[0].points[0]]);
      }

      if (changeChartSettings && canChangeChartSettings(plugins)) {
        chart.update(
          {
            tooltip: {
              hideDelay: 99999999999
            },
            plotOptions: {
              series: {
                enableMouseTracking: false,
                point: {
                  events: {
                    legendItemClick: function () {
                      return false;
                    }
                  }
                },
                events: {
                  legendItemClick: function () {
                    return false;
                  }
                }
              }
            },
            everviz: {
              inspectorMode: true
            }
          },
          true,
          false,
          false
        );
      }
    }

    setupDummyRects(chart);
    attachAdvancedLink();
  }

  function canChangeChartSettings(plugins) {
    return !(plugins && plugins['icon-chart']);
  }

  function destroy(revertChartSettings) {
    const haveStuffToDelete = duplicateXValues.length;

    [newNodes, duplicateXValues, duplicateYValues].forEach((arr) => {
      arr.forEach((a) => a.remove());
      arr = [];
    });

    changedNodeFill.forEach((arr) => arr.setAttribute('fill', 'none'));
    changedNodeFill = [];

    binders.forEach((binder) => binder());
    binders = [];

    highchartsRoot = document.querySelector('svg.highcharts-root');
    if (!highchartsRoot) return;

    highchartsRoot.setAttribute('class', highchartsRoot.getAttribute('class').replace(' in-inspector-mode', ''));

    if (
      revertChartSettings &&
      haveStuffToDelete &&
      chart.userOptions &&
      chart.userOptions.everviz &&
      chart.userOptions.everviz.inspectorMode
    ) {
      chart.update(
        {
          tooltip: {
            hideDelay: 500
          },
          plotOptions: {
            series: {
              enableMouseTracking: null,
              point: {
                events: {
                  legendItemClick: function () {}
                }
              },
              events: {
                legendItemClick: function () {}
              }
            }
          },
          everviz: {
            inspectorMode: false
          }
        },
        true,
        false,
        false
      );
    }
  }
  ///////////////////////////////////////////////////////////////////////////

  if (Highcharts) {
    Highcharts.addEvent(Highcharts.Chart, 'render', function () {
      if (isActive) {
        destroy();
        init();
      }
    });
  }

  const exports = {
    init,
    toggleActive,
    getIsActive: function () {
      return isActive;
    },
    on: events.on,
    destroy
  };

  return exports;
};
